import { useState } from "react";
import { Alert, App, Button, Card, Form, Input, Modal, Popconfirm, Select, Space } from "antd";
import { CopyOutlined, MailOutlined, PlusOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { InviteDto, Role, UserDto } from "@audit-plan/shared";
import { ApiError } from "../../api/client";
import { useUserMutations, useUsers } from "../../api/admin";
import { useSuppliers } from "../../api/suppliers";
import { Pill, fmtDT } from "../pills";
import { ResponsiveList } from "../ResponsiveList";
import { useModalProps } from "../useModalProps";
import { BulkInviteModal } from "./BulkInviteModal";
import { Sensitive } from "../Sensitive";

const ROLES: Role[] = ["admin", "manager", "auditor", "supplier", "viewer"];

function InviteLinkModal({ invite, onClose }: { invite: InviteDto | null; onClose: () => void }) {
  const { t } = useTranslation();
  const modalProps = useModalProps(520);
  const { message } = App.useApp();
  const url = invite ? (invite.inviteUrl.startsWith("http") ? invite.inviteUrl : `${window.location.origin}${invite.inviteUrl.startsWith("/") ? "" : "/"}${invite.inviteUrl}`) : "";
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      void message.success(t("a_copied"));
    } catch {
      void message.warning(t("a_copyManual"));
    }
  };
  return (
    <Modal open={!!invite} onCancel={onClose} {...modalProps} title={t("a_inviteLink")} footer={<Button onClick={onClose}>{t("close")}</Button>}>
      {invite && (
        <Space direction="vertical" style={{ width: "100%" }}>
          <Alert type="info" showIcon message={t("a_inviteLinkHelp")} />
          <div style={{ fontWeight: 600 }} data-sensitive>{invite.user.name} · {invite.user.email}</div>
          <Space.Compact style={{ width: "100%" }}>
            <Input readOnly value={url} style={{ fontSize: 16 }} onFocus={(e) => e.target.select()} />
            <Button type="primary" icon={<CopyOutlined />} onClick={copy}>{t("a_copy")}</Button>
          </Space.Compact>
          <div style={{ fontSize: 12, opacity: 0.65 }}>{t("a_expires")}: {fmtDT(invite.expiresAt)}</div>
        </Space>
      )}
    </Modal>
  );
}

export function UsersTab() {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const modalProps = useModalProps(520);
  const users = useUsers();
  const sups = useSuppliers();
  const m = useUserMutations();
  const [form] = Form.useForm();
  const [edit, setEdit] = useState<{ user?: UserDto } | null>(null);
  const [invite, setInvite] = useState<InviteDto | null>(null);
  const [bulk, setBulk] = useState(false);
  const role = Form.useWatch("role", form);
  const err = (e: unknown) => void message.error(e instanceof ApiError ? e.message : t("a_loadFail"));

  const open = (user?: UserDto) => {
    form.resetFields();
    // sensitive-ok: precompilazione del form, i campi sono marcati
    form.setFieldsValue(user ? { name: user.name, email: user.email, role: user.role, supplierId: user.supplierId } : { role: "auditor" });
    setEdit({ user });
  };
  const submit = async (v: { name: string; email: string; role: Role; supplierId?: string | null }) => {
    try {
      const supplierId = v.role === "supplier" ? (v.supplierId ?? null) : null;
      if (edit?.user) {
        await m.patch.mutateAsync({ id: edit.user.id, body: { name: v.name, role: v.role, supplierId } });
        void message.success(t("saved"));
      } else {
        // sensitive-ok: invio del form
        setInvite(await m.invite.mutateAsync({ email: v.email.trim(), name: v.name.trim(), role: v.role, supplierId }));
      }
      setEdit(null);
    } catch (e) {
      err(e);
    }
  };
  const supName = (id: string | null) => (id ? (sups.data?.find((s) => s.id === id)?.name ?? "—") : "—");
  const tone = { active: "ok", invited: "warn", disabled: "mute" } as const;

  const rowActions = (u: UserDto) => (
    <Space size={4} wrap>
      <Button size="small" type="text" onClick={() => open(u)}>{t("edit")}</Button>
      {u.status === "invited" && (
        <Button size="small" type="text" onClick={async () => { try { setInvite(await m.reinvite.mutateAsync(u.id)); } catch (e) { err(e); } }}>{t("a_reinvite")}</Button>
      )}
      {u.totpEnabled && (
        <Popconfirm
          title={t("c_resetMfaAsk", { name: u.name })}
          okText={t("yes")}
          cancelText={t("cancel")}
          onConfirm={async () => { try { await m.resetMfa.mutateAsync(u.id); void message.success(t("c_resetMfaDone")); } catch (e) { err(e); } }}
        >
          <Button size="small" type="text">{t("c_resetMfa")}</Button>
        </Popconfirm>
      )}
      <Popconfirm
        title={u.status === "disabled" ? t("a_enableAsk") : t("a_disableAsk")}
        okText={t("yes")}
        cancelText={t("cancel")}
        onConfirm={async () => { try { await m.patch.mutateAsync({ id: u.id, body: { disabled: u.status !== "disabled" } }); } catch (e) { err(e); } }}
      >
        <Button size="small" type="text" danger={u.status !== "disabled"}>{u.status === "disabled" ? t("a_enable") : t("a_disable")}</Button>
      </Popconfirm>
    </Space>
  );

  return (
    <Card
      title={t("cfgUsers")}
      styles={{ body: { padding: 0 } }}
      extra={
        <Space wrap>
          <Button size="small" icon={<MailOutlined />} onClick={() => setBulk(true)}>{t("c_inviteSuppliers")}</Button>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={() => open()}>{t("newUser")}</Button>
        </Space>
      }
    >
      <ResponsiveList<UserDto>
        size="small"
        rowKey="id"
        loading={users.isLoading}
        dataSource={users.data ?? []}
        pageSize={25}
        card={{
          title: (u) => (u.role === "supplier" ? <Sensitive>{u.name}</Sensitive> : u.name),
          subtitle: (u) => <Sensitive>{u.email}</Sensitive>,
          tags: (u) => (
            <>
              <Pill tone={tone[u.status]}>{t(`a_us_${u.status}`)}</Pill>
              <Pill tone="info">{t(`role_${u.role}`)}</Pill>
              {u.totpEnabled && <Pill tone="ok">{t("c_mfaOn")}</Pill>}
            </>
          ),
          fields: (u) => [
            [t("linkedSupplier"), supName(u.supplierId)],
            [t("a_lastLogin"), fmtDT(u.lastLoginAt)],
          ],
          footer: rowActions,
        }}
        tableProps={{ pagination: { pageSize: 25, hideOnSinglePage: true } }}
        columns={[
          { title: t("fullName"), dataIndex: "name", render: (v: string, u) => (u.role === "supplier" ? <Sensitive><b>{v}</b></Sensitive> : <b>{v}</b>) },
          { title: t("email"), dataIndex: "email", render: (v: string) => <Sensitive>{v}</Sensitive> },
          { title: t("role"), dataIndex: "role", render: (v: string) => t(`role_${v}`) },
          { title: t("linkedSupplier"), dataIndex: "supplierId", render: (v: string | null) => supName(v) },
          { title: t("status"), dataIndex: "status", render: (v: UserDto["status"]) => <Pill tone={tone[v]}>{t(`a_us_${v}`)}</Pill> },
          { title: "MFA", dataIndex: "totpEnabled", render: (v: boolean) => (v ? <Pill tone="ok">{t("c_mfaOn")}</Pill> : "—") },
          { title: t("a_lastLogin"), dataIndex: "lastLoginAt", render: (v: string | null) => fmtDT(v) },
          { title: "", align: "right" as const, render: (_, u) => rowActions(u) },
        ]}
      />
      <Modal
        open={!!edit}
        onCancel={() => setEdit(null)}
        onOk={() => form.submit()}
        okText={edit?.user ? t("save") : t("a_invite")}
        cancelText={t("cancel")}
        confirmLoading={m.invite.isPending || m.patch.isPending}
        title={edit?.user ? t("edit") : t("newUser")}
        {...modalProps}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={submit}>
          <Form.Item name="name" label={t("fullName")} rules={[{ required: true, message: t("required") }]}><Input data-sensitive /></Form.Item>
          <Form.Item name="email" label={t("email")} rules={[{ required: true, type: "email", message: t("a_emailInvalid") }]}><Input data-sensitive disabled={!!edit?.user} /></Form.Item>
          <Form.Item name="role" label={t("role")}><Select options={ROLES.map((r) => ({ value: r, label: t(`role_${r}`) }))} /></Form.Item>
          {role === "supplier" && (
            <Form.Item name="supplierId" label={t("linkedSupplier")} rules={[{ required: true, message: t("required") }]}>
              <Select showSearch optionFilterProp="label" options={(sups.data ?? []).map((s) => ({ value: s.id, label: s.name }))} />
            </Form.Item>
          )}
        </Form>
      </Modal>
      <InviteLinkModal invite={invite} onClose={() => setInvite(null)} />
      <BulkInviteModal open={bulk} onClose={() => setBulk(false)} />
    </Card>
  );
}
